import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { Table } from 'antd';
import { TableProps } from 'antd/lib';
import { ColumnsType } from 'antd/es/table';
import AppsPagination from '@nxsys/components/AppsPagination';
import AppEmpty from '@nxsys/components/AppEmpty';
import AppLoader from '@nxsys/components/AppLoader';
import { CustomTable } from './index.styled';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import {
  ImageNoDataCreated,
  ImageNoDataToShow,
  ImageNoDataFound,
} from '@nxsys/assets/images';
import AppButton from '../AppButton';
import { Icons } from '@nxsys/types/icons';

type AppTableContainerProps<T> = {
  data: T[];
  rowKey?: TableProps<T>['rowKey'];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  columns: ColumnsType<T>;
  hoverColor?: boolean;
  className?: string;
  titleEmpty?: string;
  onChangeTable?: (sorter?: { field: string; order: 'desc' | 'asc' }) => void;
  onChangePagination?: (page: number, pageSize: number) => void;
  virtual?: boolean;
  pagination?: boolean;
  showSizeChanger?: boolean;
  pageSizeOptions?: number[];
  noDataType?: 'noDataCreated' | 'noDataToShow' | 'noDataFound';
  addNewBtnName?: string;
  addNewBtnOnClick?: () => void;
  addNewIcon?: React.ReactNode;
  dataTypeName?: string;
  addNewBtn?: React.ReactNode;
  scroll?: { x?: number | string; y?: number | string };
  [x: string]: any;
};

const VIRTUAL_SCROLL_THRESHOLD = 100;

const DEFAULT_SCROLL = { x: 2000, y: 700 };

const AppTableContainer = <T,>({
  data,
  rowKey,
  columns,
  page,
  pageSize,
  total,
  loading,
  hoverColor = true,
  className,
  titleEmpty,
  onChangeTable,
  onChangePagination,
  virtual,
  pagination = true,
  showSizeChanger = true,
  pageSizeOptions,
  noDataType = 'noDataToShow',
  addNewBtnName,
  addNewBtnOnClick,
  addNewIcon = <Icons.IconPlus />,
  dataTypeName,
  addNewBtn,
  scroll: scrollProp,
  rowSelection,
  ...rest
}: AppTableContainerProps<T>) => {
  const { messages, formatMessage } = useTypedIntl();
  const tableRef = useRef<HTMLDivElement>(null);

  // Auto-enable virtual scrolling for large datasets to prevent lag
  const isVirtual = virtual ?? (data?.length ?? 0) >= VIRTUAL_SCROLL_THRESHOLD;

  // Set virtual scrollbar color (antd inline style reads --rc-virtual-list-scrollbar-bg)
  // Thickness override is in src/styles/index.css (global !important on inline width/height)
  useEffect(() => {
    if (!isVirtual || !tableRef.current) return;
    const antTable = tableRef.current.querySelector<HTMLElement>('.ant-table');
    if (antTable) {
      antTable.style.setProperty('--rc-virtual-list-scrollbar-bg', '#00795b', 'important');
    }
  }, [isVirtual, data.length]);

  const handleTableChange: TableProps<T>['onChange'] = (
    pagination,
    filters,
    sorter,
    extra,
  ) => {
    console.log('Table Change:', pagination, filters, sorter, extra);
    const sortType = {
      descend: 'desc',
      ascend: 'asc',
    };
    if (onChangeTable) {
      if (!Array.isArray(sorter) && sorter.columnKey && sorter.order) {
        onChangeTable({
          field: sorter.columnKey as string,
          order: sortType[sorter.order as 'descend' | 'ascend'] as
            | 'desc'
            | 'asc',
        });
      } else {
        onChangeTable();
      }
    }
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    if (onChangePagination) {
      onChangePagination(page, pageSize);
    }
  };
  if (!data.length) {
    const renderEmpty = () => {
      if (noDataType === 'noDataToShow') {
        return (
          <AppEmpty
            title={messages['table.noDataToShow']}
            isLoading={loading}
            imgPath={ImageNoDataToShow}
          />
        );
      }
      if (noDataType === 'noDataCreated') {
        return (
          <AppEmpty
            title={formatMessage(
              { id: 'table.noDataCreated' },
              { dataName: dataTypeName || messages['common.data'] },
            )}
            isLoading={loading}
            imgPath={ImageNoDataCreated}
            description={formatMessage(
              { id: 'table.noDataCreatedDescription' },
              { buttonName: addNewBtnName || `+ ${messages['common.addNew']}` },
            )}
            footer={
              addNewBtn || (
                <AppButton
                  name={addNewBtnName || messages['common.addNew']}
                  onClick={() => addNewBtnOnClick?.()}
                  leftIcon={addNewIcon}
                  variant="outlined"
                />
              )
            }
          />
        );
      }
      if (noDataType === 'noDataFound') {
        return (
          <AppEmpty
            title={messages['table.noDataFound']}
            isLoading={loading}
            imgPath={ImageNoDataFound}
          />
        );
      }

      return <AppEmpty title={titleEmpty} isLoading={loading} />;
    };
    return (
      <CustomTable>
        {renderEmpty()}
        <>{loading ? <AppLoader /> : null}</>
      </CustomTable>
    );
  }
  const mergedScroll = { ...DEFAULT_SCROLL, ...scrollProp };

  // In virtual mode, columns without explicit width get auto-distributed (too wide).
  // Set selection column width to prevent it from expanding and overlapping other columns.
  // Also fix "Select All" — virtual only renders ~20 rows, so onChange's selectedRows param
  // only contains visible rows. Wrap onChange to resolve full rows from data via keys.
  const resolvedRowKey = rowKey || 'id';
  const mergedRowSelection = rowSelection
    ? {
        columnWidth: isVirtual ? 48 : undefined,
        ...rowSelection,
        ...(isVirtual && rowSelection.onChange
          ? {
              onChange: (keys: React.Key[], _selectedRows: T[], info: any) => {
                const keySet = new Set(keys);
                const fullRows = data.filter((row) =>
                  keySet.has(
                    typeof resolvedRowKey === 'function'
                      ? resolvedRowKey(row)
                      : (row as any)[resolvedRowKey],
                  ),
                );
                rowSelection.onChange!(keys, fullRows, info);
              },
            }
          : {}),
      }
    : undefined;

  return (
    <CustomTable ref={tableRef}>
      <Table
        sticky
        rowKey={rowKey || 'id'}
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={false}
        scroll={mergedScroll}
        onChange={handleTableChange}
        className={clsx(
          { hoverColor: hoverColor },
          className,
        )}
        virtual={isVirtual}
        rowSelection={mergedRowSelection}
        {...rest}
      />
      {pagination && (
        <AppsPagination
          total={total}
          page={page}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          showSizeChanger={showSizeChanger}
          pageSizeOptions={pageSizeOptions}
        />
      )}
    </CustomTable>
  );
};

export default AppTableContainer;
